import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Шторка снизу — слой поверх экрана, а не отдельный экран.
 *
 * Так выбор параметра не уводит человека со страницы: закрыл шторку и
 * остался там же, где был. Mobile-first: панель прижата к низу, в зоне
 * большого пальца, и ограничена по высоте, чтобы под ней всегда был виден
 * фон и было понятно, что это слой.
 */
interface BottomSheetProps {
  title: string;
  hint?: string | undefined;
  onClose: () => void;
  /** Подпись кнопки закрытия: в окне подтверждения «Готово» читается как согласие. */
  closeLabel?: string | undefined;
  children: ReactNode;
}

export function BottomSheet({ title, hint, onClose, closeLabel = 'Готово', children }: BottomSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    panelRef.current?.querySelector('button')?.focus();

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-20 flex flex-col justify-end">
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onClose}
        className="absolute inset-0 bg-scrim/70"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex max-h-[82dvh] flex-col rounded-t-3xl border-t border-border bg-surface"
      >
        <div className="flex flex-col gap-1 px-4 pt-4 pb-2">
          <h2 className="text-lg font-semibold text-text">{title}</h2>
          {hint && <p className="text-xs text-text-3">{hint}</p>}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{children}</div>

        <div className="border-t border-border p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl border border-border-strong px-4 py-3 text-text hover:border-accent"
          >
            {closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
